import { useCallback, useEffect, useState } from "react";
import Feature from "ol/Feature";
import LineString from "ol/geom/LineString";
import Point from "ol/geom/Point";
import Polygon from "ol/geom/Polygon";
import { DrawEvent } from "ol/interaction/Draw";
import ZoomToLocation from "@avinet/adaptive-ui-maps/control/ZoomToLocation";
import Draw from "@avinet/adaptive-ui-maps/vector/interaction/Draw";
import VectorSource from "@avinet/adaptive-ui-maps/vector/source/VectorSource";
import defaultStyle from "@avinet/adaptive-ui-maps/vector/style/defaultStyle";
import VectorLayer from "@avinet/adaptive-ui-maps/vector/VectorLayer";
import { useTranslate } from "@avinet/adaptive-ui-translate";
import useGeometryPicker from "../../hooks/useGeometryPicker";
import { Icon } from "../Icon";
import MapMouseMoveListener from "./MapMouseMoveListener";
import "./PolygonGeometryPicker.scss";

const style = defaultStyle({
  strokeWidth: 2,
  pointRadius: 7,
  fillColor: "#00669944",
  strokeColor: "#006699",
});

const draftStyle = defaultStyle({
  strokeWidth: 2,
  strokeColor: "#0099ffcc",
});

const draftPointStyle = defaultStyle({
  strokeWidth: 1,
  pointRadius: 5,
  fillColor: "#0099ff",
  strokeColor: "#fff",
});

const draftAreaStyle = defaultStyle({
  strokeWidth: 0,
  strokeColor: "#0000",
  fillColor: "#0099ff33",
});

interface PolygonGeometryPickerProps {
  value: string | null;
  onValueChange: (wkt: string | null) => void;
  onValidityChange: (isValid: boolean) => void;
  autoZoom?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  mapSrid?: string;
  srid?: string;
}

const PolygonGeometryPicker = ({
  value,
  onValueChange,
  onValidityChange,
  autoZoom,
  readOnly,
  disabled,
  mapSrid,
  srid,
}: PolygonGeometryPickerProps) => {
  const t = useTranslate("PolygonGeometryPicker");
  const { feature, onChange } = useGeometryPicker<Polygon>({
    value,
    onValueChange,
    autoZoom,
    useLocationAsValue: false,
    mapSrid,
    srid,
  });

  const [draftFeature, setDraftFeature] = useState<Feature<LineString> | null>(
    null
  );
  const [draftAreaFeature, setDraftAreaFeature] =
    useState<Feature<Polygon> | null>(null);
  const [draftFeatureVisible, setDraftFeatureVisible] = useState(true);
  const [draftPointFeature, setDraftPointFeature] =
    useState<Feature<Point> | null>(null);

  const onDrawEnd = useCallback(
    (e: DrawEvent) => {
      const point = (e.feature as Feature<Point>)
        ?.getGeometry()
        ?.getCoordinates();
      if (!point) return;

      let exterior =
        feature?.getGeometry()?.getLinearRing(0)?.getCoordinates() ?? [];

      if (!Array.isArray(exterior)) {
        exterior = [exterior];
      }

      if (exterior.length > 0) exterior.pop(); // Remove point completing the polygon

      exterior.push(point); // Add the new point
      exterior.push(exterior[0]); // Re-add the start point (or new point)

      const nf = new Feature({
        geometry: new Polygon([exterior]),
      });

      setDraftFeatureVisible(true);
      onChange(nf);
    },
    [feature, onChange]
  );

  const onMouseMove = useCallback(
    (end) => {
      if (!feature || !end) return;
      const coords = feature.getGeometry()?.getLinearRing(0)?.getCoordinates();
      if (!coords || !coords.length) return;
      setDraftFeature(
        new Feature({
          geometry: new LineString([coords[coords.length - 2], end, coords[0]]),
        })
      );
      setDraftAreaFeature(
        new Feature({
          geometry: new Polygon([[coords[coords.length - 2], end, coords[0]]]),
        })
      );
    },
    [feature]
  );

  useEffect(() => {
    setDraftPointFeature(null);
    if (!feature) return;
    const coords = feature.getGeometry()?.getLinearRing(0)?.getCoordinates();
    if (!coords || !coords.length) return;
    setDraftPointFeature(
      new Feature({
        geometry: new Point(coords[coords.length - 1]),
      })
    );
  }, [feature]);

  useEffect(() => {
    setDraftFeature(null);
    setDraftAreaFeature(null);

    const coords = feature?.getGeometry()?.getCoordinates();
    onValidityChange(!!coords ? coords[0].length >= 4 : false);
  }, [feature, onValidityChange]);

  const undo = useCallback(() => {
    const exterior = feature?.getGeometry()?.getLinearRing(0)?.getCoordinates();
    if (!exterior) return;
    exterior.pop();
    exterior.pop();
    if (exterior.length > 1)
      onChange(
        new Feature({ geometry: new Polygon([[...exterior, exterior[0]]]) })
      );
    else onChange(null);
  }, [feature, onChange]);

  return (
    <div className="geometry-picker geometry-picker-polygon">
      <ZoomToLocation title={t("location")} />
      <div className="edit-buttons">
        {feature && (
          <button
            type="button"
            className="btn map btn-reset warning"
            onClick={() => onChange(null)}
          >
            <Icon name="delete" /> {t("reset")}
          </button>
        )}
        {feature && (
          <button type="button" className="btn map btn-undo" onClick={undo}>
            <Icon name="subtractCircle" /> {t("undo")}
          </button>
        )}
        {draftFeatureVisible &&
          feature &&
          (feature.getGeometry()?.getLinearRing(0)?.getCoordinates()?.length ??
            0) > 2 && (
            <button
              type="button"
              className="btn map btn-done"
              onClick={() => setDraftFeatureVisible(false)}
            >
              <Icon name="checkmark" /> {t("done")}
            </button>
          )}
      </div>
      <VectorLayer style={style} zIndex={1}>
        <VectorSource features={(feature && [feature]) || []} />
        {!readOnly && !disabled && <Draw type="Point" onDrawEnd={onDrawEnd} />}
      </VectorLayer>
      {draftFeatureVisible && (
        <>
          <VectorLayer style={draftAreaStyle} zIndex={1}>
            <VectorSource
              features={(draftAreaFeature && [draftAreaFeature]) || []}
            />
          </VectorLayer>
          <VectorLayer style={draftStyle} zIndex={2}>
            <VectorSource features={(draftFeature && [draftFeature]) || []} />
          </VectorLayer>
          <VectorLayer style={draftPointStyle} zIndex={3}>
            <VectorSource
              features={(draftPointFeature && [draftPointFeature]) || []}
            />
          </VectorLayer>
          <MapMouseMoveListener onMouseMove={onMouseMove} />
        </>
      )}
    </div>
  );
};

export default PolygonGeometryPicker;
